import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listClaims } from '../services/api';

const STATUS_COLORS = {
  INTAKE_COMPLETE: '#38a169', INTAKE_IN_PROGRESS: '#d69e2e', VALIDATING: '#3182ce',
  UNDER_REVIEW: '#805ad5', APPROVED: '#38a169', DENIED: '#e53e3e',
  PARTIAL_APPROVED: '#dd6b20', MANUAL_REVIEW: '#d69e2e', SETTLED: '#2f855a', CLOSED: '#718096',
};

export default function ClaimsList() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchClaims = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await listClaims(statusFilter || null);
      setClaims(data.claims || []);
    } catch (err) {
      setError('Failed to fetch claims: ' + (err.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Claims Dashboard</h2>
        <div style={s.controls}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={s.select}>
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
          </select>
          <button onClick={fetchClaims} style={s.btnRefresh}>Refresh</button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {loading ? <p style={s.loading}>Loading claims...</p> : claims.length === 0 ? <p style={s.empty}>No claims found.</p> : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Claim ID</th><th style={s.th}>Patient</th><th style={s.th}>Hospital</th>
              <th style={s.th}>Admission</th><th style={s.th}>Amount</th><th style={s.th}>Status</th><th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {claims.map(claim => (
              <tr key={claim.claimId} style={s.tr}>
                <td style={s.td}>{claim.claimId}</td>
                <td style={s.td}>{claim.patientName || claim.patientId}</td>
                <td style={s.td}>{claim.hospitalName || claim.hospitalId}</td>
                <td style={s.td}>{claim.admissionDate}</td>
                <td style={s.td}>{Number(claim.totalClaimedAmount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</td>
                <td style={s.td}>
                  <span style={{ ...s.badge, background: STATUS_COLORS[claim.status] || '#718096' }}>
                    {(claim.status || '').replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={s.td}>
                  <button onClick={() => navigate(`/claims/${claim.claimId}`)} style={s.btnView}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const s = {
  container: { maxWidth: 1100, margin: '0 auto', padding: 24, fontFamily: 'system-ui, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { color: '#1a365d', margin: 0 },
  controls: { display: 'flex', gap: 8 },
  select: { padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14 },
  btnRefresh: { padding: '8px 16px', background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: 6, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { textAlign: 'left', padding: '12px 16px', background: '#edf2f7', color: '#2d3748', fontSize: 13, fontWeight: 600, borderBottom: '2px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #edf2f7' },
  td: { padding: '10px 16px', fontSize: 14, color: '#4a5568' },
  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 12, color: '#fff', fontSize: 12, fontWeight: 600 },
  btnView: { padding: '4px 12px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  error: { background: '#fed7d7', color: '#c53030', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },
  loading: { textAlign: 'center', color: '#718096', padding: 40 },
  empty: { textAlign: 'center', color: '#a0aec0', padding: 40 },
};
